import React, { useEffect, useState, useRef, useCallback } from "react";
import Modal from "react-bootstrap/Modal";
import { GoogleMap, Marker, LoadScript } from "@react-google-maps/api";
import axiosConfig from "../Service/AxiosConfig";
import { useAuth } from "../Context/AuthContext";
import { useLocationContext } from "../Context/LocationContext";
import "./AdressModal.css";

const librariesArray = ["places"];

const AddressModal = ({ isOpen, onClose, mode, form, setForm }) => {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [apiKey, setApiKey] = useState("");

  const mapRef = useRef(null);
  const placesServiceRef = useRef(null);
  const debounceTimer = useRef(null);

  const { user } = useAuth();
  const { setLocation } = useLocationContext();
  const user_id = user?.id;

  useEffect(() => {
    axiosConfig
      .get("/masters/googlemapkey/")
      .then((res) => setApiKey(res?.data?.results[0]?.key));
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    setSelectedLocation(null);
    setSearchQuery("");

    if (mode === "permanent" && form.permanent_lat && form.permanent_lng) {
      setSelectedLocation({
        lat: form.permanent_lat,
        lng: form.permanent_lng,
      });
      setSearchQuery(form.permanent_address || "");
    }

    if (mode === "current" && form.current_lat && form.current_lng) {
      setSelectedLocation({
        lat: form.current_lat,
        lng: form.current_lng,
      });
      setSearchQuery(form.current_address || "");
    }
  }, [isOpen, mode]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    if (!placesServiceRef.current && window.google) {
      placesServiceRef.current = new window.google.maps.places.PlacesService(
        document.createElement("div"),
      );
    }
  }, []);

  const handlePlaceSearch = useCallback((query) => {
    if (!query.trim()) return setSearchResults([]);
    if (!window.google?.maps?.places) return;

    if (!placesServiceRef.current) {
      placesServiceRef.current = new window.google.maps.places.PlacesService(
        document.createElement("div"),
      );
    }

    placesServiceRef.current.textSearch({ query }, (results, status) => {
      if (
        status === window.google.maps.places.PlacesServiceStatus.OK &&
        results
      ) {
        setSearchResults(results);
      } else {
        setSearchResults([]);
      }
    });
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => handlePlaceSearch(value), 300);
  };

  const handleResultClick = (result) => {
    if (!result.geometry?.location) return;

    const latitude = result.geometry.location.lat();
    const longitude = result.geometry.location.lng();

    setSelectedLocation({ lat: latitude, lng: longitude });
    setSearchQuery(result.formatted_address);
    setSearchResults([]);
  };

  const handleMapClick = async (e) => {
    const loc = e.latLng.toJSON();
    setSelectedLocation(loc);

    const res = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${loc.lat},${loc.lng}&key=${apiKey}`,
    );
    const data = await res.json();

    const address = data?.results?.[0]?.formatted_address || "";
    setSearchQuery(address);
  };
  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by this browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setSelectedLocation({ lat, lng });
        try {
          const res = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`,
          );

          const data = await res.json();

          if (data.status !== "OK") {
            console.error("Geocode error:", data);
            alert("Unable to fetch address from coordinates.");
            return;
          }

          const address =
            data.results?.[0]?.formatted_address || "Address not found";

          setForm((prev) => ({
            ...prev,
            address,
            latitude: lat,
            longitude: lng,
          }));

          setSearchQuery(address);

          if (mode === "browser") {
            setLocation({
              browsing_address: address,
              browsing_lat: lat,
              browsing_lng: lng,
            });
          }
        } catch (error) {
          console.error("Fetch error:", error);
          alert("Error fetching address.");
        }
      },
      (error) => {
        console.error("Geolocation error:", error);

        if (error.code === 1) {
          alert("Location permission denied.");
        } else if (error.code === 2) {
          alert("Location unavailable.");
        } else if (error.code === 3) {
          alert("Location request timed out.");
        } else {
          alert("Unknown error getting location.");
        }
      },
    );
  };
  /* ------------------- Save Address ------------------- */
  const handleSaveAddress = async () => {
    if (!selectedLocation || !searchQuery) {
      alert("Please select a valid location");
      return;
    }

    const { lat, lng } = selectedLocation;

    if (mode === "browser") {
      setLocation({
        browsing_address: searchQuery,
        browsing_lat: lat,
        browsing_lng: lng,
      });
      onClose();
      return;
    }

    const payload = {
      address: searchQuery,
      latitude: lat,
      longitude: lng,
      address_type: mode === "current" ? "Current" : "Permanent",
      user: user_id,
    };

    try {
      const existingId =
        mode === "permanent"
          ? form.permanent_address_id
          : form.current_address_id;

      let response;

      if (existingId) {
        response = await axiosConfig.patch(
          `/accounts/address/${existingId}/`,
          payload,
        );
      } else {
        response = await axiosConfig.post(`/accounts/address/`, payload);
      }

      const savedId = response.data.id;

      if (mode === "permanent") {
        setForm((prev) => ({
          ...prev,
          permanent_address: searchQuery,
          permanent_lat: lat,
          permanent_lng: lng,
          permanent_address_id: savedId,
        }));
      } else {
        setForm((prev) => ({
          ...prev,
          current_address: searchQuery,
          current_lat: lat,
          current_lng: lng,
          current_address_id: savedId,
        }));
      }

      onClose();
    } catch (err) {
      console.error("Error saving address:", err);
    }
  };

  return (
    <Modal centered size="lg" show={isOpen} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Add Address</Modal.Title>
      </Modal.Header>
      <div className="address-modal-container p-3">
        <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search for a place"
            className="address-search-input"
          />
          <button
            className="use-current-location-btn"
            onClick={handleUseCurrentLocation}
          >
            Use My Current Location
          </button>
        </div>
        {searchResults.length > 0 && (
          <ul className="address-list-container">
            {searchResults.map((result) => (
              <li
                key={result.place_id}
                onClick={() => handleResultClick(result)}
              >
                {result.formatted_address}
              </li>
            ))}
          </ul>
        )}

        {apiKey && (
          <LoadScript googleMapsApiKey={apiKey} libraries={librariesArray}>
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "300px" }}
              center={selectedLocation || { lat: 17.38714, lng: 78.491684 }}
              zoom={13}
              onLoad={onMapLoad}
              onClick={handleMapClick}
            >
              {selectedLocation && <Marker position={selectedLocation} />}
            </GoogleMap>
          </LoadScript>
        )}
        <div className="row mt-3">
          <div className="col-md-6">
            <label>Latitude</label>
            <input
              className="latlong-box"
              value={selectedLocation?.lat || ""}
              readOnly
            />
          </div>
          <div className="col-md-6">
            <label>Longitude</label>
            <input
              className="latlong-box"
              value={selectedLocation?.lng || ""}
              readOnly
            />
          </div>
        </div>

        <label className="address-label mt-3">Selected Address</label>
        <textarea
          className="current-address-box"
          value={searchQuery}
          readOnly
          rows={2}
        />
        <button className="address-save-btn mt-3" onClick={handleSaveAddress}>
          Save Address
        </button>
      </div>
    </Modal>
  );
};

export { AddressModal };
